import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Input } from 'ui-components/components/input';
import { WeightInput } from 'ui-components/components/weight-input';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { Text, TextType } from 'ui-components/components/text/index';
import { DropdownField } from 'ui-components/components/dropdown/dropdown-field';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import ShipFromDropdown from '../../../../../fulfillment-planner/ship-from/ship-from-dropdown';
import { ShipFromSearchableDropdown } from '../../../../../fulfillment-planner/ship-from/ship-from-searchable-dropdown';
import { useEnableShipFromSearchableDropdown } from '../../../../../feature-flags/hooks/use-enable-ship-from-searchable-dropdown';
import { StateDropdown } from '../../../../../address/state-dropdown';
import { CountryDropdown } from '../../../../../address/country-dropdown';
import { FlexRow } from 'ui-components/components/flex/row';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';

import { convertFromWeightUnitEnum } from '../../../../../services/unit-services/weights/weight-unit-enum-converter';
import { convertFromDimensionUnitEnum } from '../../../../../services/unit-services/dimensions/dimension-unit-enum-converter';
import type { DimensionUnit } from '../../../../../prop-types/dimenstion-unit';
import Global from '../../../../../store/global-selectors';
import type { LtlAddress, LtlHandlingUnit, LtlCommodity } from '@shipstation/client-common/types/ltl';
import { FREIGHT_CLASS_OPTIONS, PACKAGING_TYPE_OPTIONS } from '../../../constants';

import { useFreightQuote, createEmptyCommodity, createEmptyHandlingUnit } from '../freight-quote-context';
import { freightConfigFormMessages } from './freight-config-form-messages';
import style from './freight-config-form.module.less';

type FreightConfigFormProps = {
  onGetQuotes: () => void;
};

export const FreightConfigForm = ({ onGetQuotes }: FreightConfigFormProps) => {
  const intl = useIntl();
  const { state: wizardState, localDispatch } = useFreightQuote();

  const shipFroms = useSelector(Global.selectSellerShipFroms);
  const selectedShipFromId = wizardState.selectedShipFromId;
  const destinationAddress: LtlAddress = wizardState.destinationAddress;
  const shipmentDate: string = wizardState.shipmentDate;
  const handlingUnits: LtlHandlingUnit[] = wizardState.handlingUnits;
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const weightInputUnit = convertFromWeightUnitEnum(sellerDefaultWeightUnit);
  const dimensionInputUnit = convertFromDimensionUnitEnum(sellerDefaultDimensionUnit) as DimensionUnit;

  const shouldUseSearchableDropdown = useEnableShipFromSearchableDropdown();

  const shipFromOptions = useMemo(
    () => (shipFroms ?? []).map((sf: { shipFromId: number; name: string }) => ({ id: sf.shipFromId, name: sf.name })),
    [shipFroms],
  );

  useEffect(() => {
    if (!shipmentDate) {
      const today = new Date().toISOString().split('T')[0];
      localDispatch({ type: 'UPDATE_SHIPMENT_DATE', shipmentDate: today });
    }
    if (!destinationAddress.countryCode) {
      localDispatch({
        type: 'UPDATE_DESTINATION_ADDRESS',
        address: { ...destinationAddress, countryCode: 'US' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShipFromChange = useCallback(
    (shipFromId: string) => {
      localDispatch({ type: 'SET_SELECTED_SHIP_FROM', shipFromId });
    },
    [localDispatch],
  );

  const handleDestinationFieldChange = useCallback(
    (field: keyof LtlAddress, value: string) => {
      localDispatch({ type: 'UPDATE_DESTINATION_ADDRESS', address: { ...destinationAddress, [field]: value } });
    },
    [localDispatch, destinationAddress],
  );

  const handleAddHandlingUnit = useCallback(() => {
    localDispatch({ type: 'ADD_HANDLING_UNIT', handlingUnit: createEmptyHandlingUnit() });
  }, [localDispatch]);

  const handleRemoveHandlingUnit = useCallback(
    (handlingUnitId: string) => {
      localDispatch({ type: 'REMOVE_HANDLING_UNIT', handlingUnitId });
    },
    [localDispatch],
  );

  const handleHandlingUnitFieldChange = useCallback(
    (unitId: string, field: keyof LtlHandlingUnit, value: string | number | boolean) => {
      const updatedUnits = handlingUnits.map((unit) => (unit.id === unitId ? { ...unit, [field]: value } : unit));
      localDispatch({ type: 'UPDATE_HANDLING_UNITS', handlingUnits: updatedUnits });
    },
    [localDispatch, handlingUnits],
  );

  const handleHandlingUnitDimensionsChange = useCallback(
    (unitId: string, dimensions: { length?: number; width?: number; height?: number }) => {
      const updatedUnits = handlingUnits.map((unit) =>
        unit.id === unitId
          ? {
              ...unit,
              length: dimensions.length ?? unit.length,
              width: dimensions.width ?? unit.width,
              height: dimensions.height ?? unit.height,
            }
          : unit,
      );
      localDispatch({ type: 'UPDATE_HANDLING_UNITS', handlingUnits: updatedUnits });
    },
    [localDispatch, handlingUnits],
  );

  const handleAddCommodity = useCallback(
    (unitId: string) => {
      localDispatch({ type: 'ADD_COMMODITY', handlingUnitId: unitId, commodity: createEmptyCommodity() });
    },
    [localDispatch],
  );

  const handleRemoveCommodity = useCallback(
    (unitId: string, commodityId: string) => {
      localDispatch({ type: 'REMOVE_COMMODITY', handlingUnitId: unitId, commodityId });
    },
    [localDispatch],
  );

  const handleCommodityFieldChange = useCallback(
    (unitId: string, commodity: LtlCommodity, field: keyof LtlCommodity, value: string | number | boolean) => {
      localDispatch({
        type: 'UPDATE_COMMODITY',
        handlingUnitId: unitId,
        commodityId: commodity.id,
        commodity: { ...commodity, [field]: value },
      });
    },
    [localDispatch],
  );

  const renderShipFromSection = () => (
    <div className={style.section}>
      <Text textType={TextType.H4} value={<FormattedMessage {...freightConfigFormMessages.shipFrom} />} />
      <div className={style['ship-from-field']}>
        {shouldUseSearchableDropdown ? (
          <ShipFromSearchableDropdown onChange={handleShipFromChange} selectedShipFromId={selectedShipFromId} shipFroms={shipFromOptions} />
        ) : (
          <ShipFromDropdown onChange={handleShipFromChange} selectedShipFromId={selectedShipFromId} shipFroms={shipFromOptions} />
        )}
      </div>
    </div>
  );

  const renderShipToSection = () => (
    <div className={style.section}>
      <Text textType={TextType.H4} value={<FormattedMessage {...freightConfigFormMessages.shipTo} />} />
      <FlexRow className={style['address-row']} gap={Gap.Space8}>
        <div className={style.field}>
          <Text textType={TextType.Label} value={<FormattedMessage {...freightConfigFormMessages.city} />} />
          <Input
            className={style['text-input']}
            onChange={(value: string) => handleDestinationFieldChange('city', value)}
            type='text'
            value={destinationAddress.city}
          />
        </div>
        <div className={style.field}>
          <Text textType={TextType.Label} value={<FormattedMessage {...freightConfigFormMessages.state} />} />
          <StateDropdown
            componentClassName={style['state-dropdown']}
            countryCode='US'
            onChange={(value: string) => handleDestinationFieldChange('state', value)}
            value={destinationAddress.state}
          />
        </div>
      </FlexRow>
      <FlexRow className={style['address-row']} gap={Gap.Space8}>
        <div className={style.field}>
          <Text textType={TextType.Label} value={<FormattedMessage {...freightConfigFormMessages.postalCode} />} />
          <Input
            className={style['text-input']}
            onChange={(value: string) => handleDestinationFieldChange('postalCode', value)}
            type='text'
            value={destinationAddress.postalCode}
          />
        </div>
        <div className={style.field}>
          <Text textType={TextType.Label} value={<FormattedMessage {...freightConfigFormMessages.country} />} />
          <CountryDropdown
            componentClassName={style['country-dropdown']}
            onChange={(value: string) => handleDestinationFieldChange('countryCode', value)}
            value={destinationAddress.countryCode}
          />
        </div>
      </FlexRow>
    </div>
  );

  const renderCommodity = (unitId: string, commodity: LtlCommodity, commodityIndex: number, canRemove: boolean) => (
    <div key={commodity.id} className={style['commodity-card']}>
      <FlexRow className={style['commodity-header']}>
        <Text
          textType={TextType.Label}
          value={intl.formatMessage(freightConfigFormMessages.commodityTitle, { index: commodityIndex + 1 })}
        />
        {canRemove && (
          <Button onClick={() => handleRemoveCommodity(unitId, commodity.id)} theme={THEME_CONSTANTS.link}>
            <Text textType={TextType.Caption} value={<FormattedMessage {...freightConfigFormMessages.removeCommodity} />} />
          </Button>
        )}
      </FlexRow>
      <div className={style.field}>
        <Text textType={TextType.Label} value={<FormattedMessage {...freightConfigFormMessages.freightClass} />} />
        <DropdownField
          onItemSelected={(value: string) => handleCommodityFieldChange(unitId, commodity, 'freightClass', value)}
          options={FREIGHT_CLASS_OPTIONS}
          selectedItemValue={commodity.freightClass}
        />
      </div>
      <div className={style.field}>
        <Text textType={TextType.Label} value={<FormattedMessage {...freightConfigFormMessages.weight} />} />
        <WeightInput
          onChange={(weightValue: number) => handleCommodityFieldChange(unitId, commodity, 'weight', weightValue)}
          unit={weightInputUnit}
          weight={commodity.weight}
        />
      </div>
    </div>
  );

  const renderHandlingUnit = (unit: LtlHandlingUnit, unitIndex: number) => (
    <div key={unit.id} className={style['handling-unit-card']}>
      <FlexRow className={style['handling-unit-header']}>
        <Text textType={TextType.Label} value={intl.formatMessage(freightConfigFormMessages.handlingUnitTitle, { index: unitIndex + 1 })} />
        {handlingUnits.length > 1 && (
          <Button onClick={() => handleRemoveHandlingUnit(unit.id)} theme={THEME_CONSTANTS.link}>
            <Text textType={TextType.Caption} value={<FormattedMessage {...freightConfigFormMessages.removeHandlingUnit} />} />
          </Button>
        )}
      </FlexRow>
      <div className={style.field}>
        <Text textType={TextType.Label} value={<FormattedMessage {...freightConfigFormMessages.packaging} />} />
        <DropdownField
          onItemSelected={(value: string) => handleHandlingUnitFieldChange(unit.id, 'packagingType', value)}
          options={PACKAGING_TYPE_OPTIONS}
          selectedItemValue={unit.packagingType}
        />
      </div>
      <div className={style.field}>
        <Text textType={TextType.Label} value={<FormattedMessage {...freightConfigFormMessages.dimensions} />} />
        <DimensionInput
          className={style['dimension-input-compact']}
          dimensions={{ length: unit.length, width: unit.width, height: unit.height, unit: dimensionInputUnit }}
          onChange={(dimensions: { length?: number; width?: number; height?: number }) =>
            handleHandlingUnitDimensionsChange(unit.id, dimensions)
          }
          unit={dimensionInputUnit}
        />
      </div>
      <FlexColumn className={style['commodities-section']} gap={Gap.Space8}>
        {unit.commodities.map((commodity, commodityIndex) =>
          renderCommodity(unit.id, commodity, commodityIndex, unit.commodities.length > 1),
        )}
        <Button onClick={() => handleAddCommodity(unit.id)} theme={THEME_CONSTANTS.link}>
          <Text textType={TextType.Caption} value={<FormattedMessage {...freightConfigFormMessages.addCommodity} />} />
        </Button>
      </FlexColumn>
    </div>
  );

  const renderHandlingUnitsSection = () => (
    <div className={style.section}>
      <Text textType={TextType.H4} value={<FormattedMessage {...freightConfigFormMessages.handlingUnits} />} />

      {handlingUnits.map((unit, index) => renderHandlingUnit(unit, index))}
      <Button onClick={handleAddHandlingUnit} theme={THEME_CONSTANTS.link}>
        <Text textType={TextType.Caption} value={<FormattedMessage {...freightConfigFormMessages.addHandlingUnit} />} />
      </Button>
    </div>
  );

  return (
    <div className={style.form}>
      <div className={style.header}>
        <FormattedMessage {...freightConfigFormMessages.configureRates} />
      </div>
      <div className={style.body}>
        {renderShipFromSection()}
        {renderShipToSection()}
        {renderHandlingUnitsSection()}
      </div>
      <div className={style.footer}>
        <Button onClick={onGetQuotes} theme='primary'>
          <FormattedMessage {...freightConfigFormMessages.getQuotes} />
        </Button>
      </div>
    </div>
  );
};
